import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import Layout from "../../Component/Layout";
import Select from "react-select";
import axios from "axios";
import { FaChevronRight, FaChevronDown, FaCubes, FaGripLines, FaTrash, FaEdit } from "react-icons/fa";
import { toast } from "react-toastify";


const BomRFQ = forwardRef(({ isModal = false, preSelectedCode = null, rfqId = null, rfqItemId = null, isReadOnly = false }, ref) => {
  const [productOptions, setProductOptions] = useState([]);
  const [bomTree, setBomTree] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(false);
  
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedDragProduct, setSelectedDragProduct] = useState(null);
  
  const [altModal, setAltModal] = useState({
    show: false, alternates: [], targetNode: null, originalProduct: null, selectedValue: "", selectedLabel: ""
  });

  const [detailsModal, setDetailsModal] = useState({
    show: false, targetNode: null, product: null, 
    formData: { quantity: "1", uom: "", valid_from: "", valid_to: "", change_number: "", base_unit: "1000", plant_location: "Penya", alternative_bom: "10" }
  });

  useImperativeHandle(ref, () => ({
    saveBom: handleSaveBOM
  }));

  // LOAD SMART BOM (Custom first, fallback to Master)
  useEffect(() => {
    if (preSelectedCode && rfqItemId) {
      setLoading(true);

      const fetchTreeData = async () => {
        try {
          // 1. FETCH FULL PRODUCT DETAILS FIRST TO GUARANTEE UOM AND DESCRIPTION
          let productsList = [];
          try {
              const prodRes = await axios.get("https://master.erp.sbeecables.com/api/bom/products");
              productsList = prodRes.data.data.map(p => {
                  let autoCat = "ROH";
                  if (p.selectedType) {
                      const type = p.selectedType.toLowerCase().trim();
                      if (type === "semi finished") autoCat = "HALB";
                      else if (type === "finished") autoCat = "FERT";
                  }
                  return { value: p.productCode, label: p.productCode, description: p.description || "", uom: p.uom || "NOS", item_category: autoCat };
              });
              setProductOptions(productsList);
          } catch(e) { console.error("Failed to load Product Master", e); }

          // Helper to safely get Product Info
          const getProdInfo = (code) => {
              const found = productsList.find(p => p.value === code);
              return found || { description: "", uom: "NOS", item_category: "ROH" };
          };

          // 2. Check if Custom RFQ BOM already exists
          let customExists = false;
          let customRows = [];
          
          try {
              const customRes = await axios.get(`https://master.erp.sbeecables.com/api/bom/custom-rfq-bom/${rfqItemId}`);
              if (customRes.data.exists && customRes.data.data.length > 0) {
                  customExists = true;
                  customRows = customRes.data.data;
              }
          } catch (apiErr) {
              console.warn("Custom RFQ BOM Route not found or failed, falling back to Master BOM...");
          }

          if (customExists) {
             // --- RENDER CUSTOM RFQ BOM ---
             const map = {};
             map[preSelectedCode] = { 
                 id: preSelectedCode, productCode: preSelectedCode, label: preSelectedCode, children: [], 
                 headerDetails: { base_unit: 1000, plant_location: 'Penya', alternative_bom: '10', unit_of_measurement: getProdInfo(preSelectedCode).uom } 
             };

             customRows.forEach(row => {
                 if (!map[row.product_code]) {
                     map[row.product_code] = { 
                         id: row.product_code, productCode: row.product_code, label: row.product_code, children: [], 
                         headerDetails: { base_unit: row.base_unit, plant_location: row.plant_location, alternative_bom: row.alternative_bom, unit_of_measurement: row.unit_of_measurement || getProdInfo(row.product_code).uom } 
                     };
                 }
                 if (!map[row.component_product]) {
                     map[row.component_product] = { 
                         id: row.component_product, productCode: row.component_product, label: row.component_product, children: [],
                         quantity: row.quantity, uom: row.uom || getProdInfo(row.component_product).uom,
                         details: { item: row.item, item_category: row.item_category, component_description: row.component_description || getProdInfo(row.component_product).description, assembly: row.assembly, valid_from: row.valid_from ? row.valid_from.split('T')[0] : "", valid_to: row.valid_to ? row.valid_to.split('T')[0] : "", change_number: row.change_number }
                     };
                 } else {
                     map[row.component_product].quantity = row.quantity;
                     map[row.component_product].uom = row.uom || getProdInfo(row.component_product).uom;
                     map[row.component_product].details = { item: row.item, item_category: row.item_category, component_description: row.component_description || getProdInfo(row.component_product).description, assembly: row.assembly, valid_from: row.valid_from ? row.valid_from.split('T')[0] : "", valid_to: row.valid_to ? row.valid_to.split('T')[0] : "", change_number: row.change_number };
                 }
             });

             customRows.forEach(row => {
                 if (map[row.product_code] && map[row.component_product]) {
                     const childExists = map[row.product_code].children.find(c => c.id === row.component_product);
                     if(!childExists) map[row.product_code].children.push(map[row.component_product]);
                 }
             });

             setBomTree(map[preSelectedCode]);
             setExpanded({ [preSelectedCode]: true });

          } else {
             // --- RENDER MASTER BOM --- (Fallback)
             const flowRes = await axios.get(`https://master.erp.sbeecables.com/api/bom-flow/${preSelectedCode}`);
             const nodes = flowRes.data.nodes || [];
             const edges = flowRes.data.edges || [];

             // NEW LOGIC: Fetch existing components for EVERY sub-assembly in the tree to preserve Dates and Descriptions
             let allMasterComponents = [];
             try {
                const headerRes = await axios.get("https://master.erp.sbeecables.com/api/bom");
                const allHeaders = headerRes.data.data || [];
                
                // Find all headers that match our current tree nodes
                const relevantHeaders = allHeaders.filter(h => {
                   const codes = h.product_code ? h.product_code.split(",").map(c => c.trim()) : [];
                   return codes.includes(preSelectedCode) || nodes.some(n => {
                       let pCode = n.id;
                       const match = n.label.match(/(.*?)\s*\((.*?)\s+(.*?)\)/);
                       if (match) pCode = match[1].trim(); else pCode = n.label;
                       return codes.includes(pCode);
                   });
                });

                // Fetch details for all matched headers
                await Promise.all(relevantHeaders.map(async (h) => {
                   try {
                       const detailRes = await axios.get(`https://master.erp.sbeecables.com/api/bom/${h.bom_id}`);
                       allMasterComponents = [...allMasterComponents, ...(detailRes.data.data.components || [])];
                   } catch(e) {}
                }));
             } catch (err) { console.warn("Could not fetch Master details", err); }

             if (nodes.length > 0) {
                 const map = {};
                 nodes.forEach(n => {
                     let pCode = n.id;
                     let qty = "";
                     let uom = "";
                     const match = n.label.match(/(.*?)\s*\((.*?)\s+(.*?)\)/);
                     if (match) { pCode = match[1].trim(); qty = parseFloat(match[2]); uom = match[3]; }
                     else { pCode = n.label; }

                     const pInfo = getProdInfo(pCode);
                     
                     // Find the component across ALL fetched master assemblies
                     const extComp = allMasterComponents.find(c => c.component_product === pCode);

                     map[n.id] = { 
                         ...n, productCode: pCode, label: pCode, 
                         quantity: extComp ? extComp.quantity : qty, 
                         uom: extComp ? extComp.uom : (uom || pInfo.uom), 
                         headerDetails: { base_unit: 1000, plant_location: 'Penya', alternative_bom: '10', unit_of_measurement: pInfo.uom },
                         details: { 
                             item: extComp?.item || "", 
                             item_category: extComp?.item_category || pInfo.item_category, 
                             component_description: extComp?.component_description || pInfo.description, // Fallback to Master Product Desc
                             assembly: extComp?.assembly || "No", 
                             valid_from: extComp?.valid_from ? extComp.valid_from.split('T')[0] : "", 
                             valid_to: extComp?.valid_to ? extComp.valid_to.split('T')[0] : "", 
                             change_number: extComp?.change_number || "" 
                         },
                         children: [] 
                     };
                 });

                 edges.forEach(e => {
                     if (map[e.source] && map[e.target]) map[e.source].children.push(map[e.target]);
                 });
                 setBomTree(map[nodes[0].id]);
                 setExpanded({ [nodes[0].id]: true });
             } else {
                 const pInfo = getProdInfo(preSelectedCode);
                 const rootId = `${preSelectedCode}-root`; 
                 setBomTree({ id: rootId, productCode: preSelectedCode, label: preSelectedCode, children: [], headerDetails: { base_unit: 1000, plant_location: 'Penya', alternative_bom: '10', unit_of_measurement: pInfo.uom } });
                 setExpanded({ [rootId]: true });
             }
          }
        } catch (err) {
          console.error("Error building BOM Tree", err);
          setBomTree({ id: `${preSelectedCode}-root`, productCode: preSelectedCode, label: preSelectedCode, children: [] });
        } finally {
          setLoading(false);
        }
      };

      fetchTreeData();
    }
  }, [preSelectedCode, rfqItemId]);

  const toggleNode = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const handleDragStart = (e, product) => {
    if(!isEditMode) return;
    e.dataTransfer.setData("draggedProduct", JSON.stringify(product));
  };

  const handleDragOver = (e) => {
    if(!isEditMode) return;
    e.preventDefault();
  };

  const openDetailsModal = (targetNode, product) => {
    setDetailsModal({ show: true, targetNode, product, formData: { quantity: "1", uom: product.uom || "NOS", valid_from: "", valid_to: "", change_number: "", base_unit: "1000", plant_location: "Penya", alternative_bom: "10" } });
  };

  const handleDrop = async (e, targetNode) => {
    if(!isEditMode) return;
    e.preventDefault();
    e.stopPropagation();

    const productData = e.dataTransfer.getData("draggedProduct");
    if (!productData) return;
    const product = JSON.parse(productData);

    if (product.value === targetNode.productCode) {
      toast.warning("Cannot add a product directly to itself!"); return;
    }

    try {
      const res = await axios.get(`https://master.erp.sbeecables.com/api/alternate-products/${product.value}`);
      const alternates = res.data;
      if (alternates && alternates.length > 0) {
        setAltModal({ show: true, alternates: alternates, targetNode: targetNode, originalProduct: product, selectedValue: product.value, selectedLabel: product.label });
      } else {
        openDetailsModal(targetNode, product);
      }
    } catch (err) {
      openDetailsModal(targetNode, product);
    }
  };

  const handleConfirmAlternate = () => {
    let finalProduct = altModal.originalProduct;
    if (altModal.selectedValue !== altModal.originalProduct.value) {
       const selectedOpt = productOptions.find(p => p.value === altModal.selectedValue);
       finalProduct = selectedOpt ? selectedOpt : { value: altModal.selectedValue, label: altModal.selectedValue, uom: "NOS", item_category: "ROH", description: "" };
    }
    const tNode = altModal.targetNode;
    setAltModal({ show: false, alternates: [], targetNode: null, originalProduct: null, selectedValue: "", selectedLabel: "" });
    openDetailsModal(tNode, finalProduct);
  };

  const handleConfirmDropDetails = () => {
    const { targetNode, product, formData } = detailsModal;
    const newNodeId = `${product.value}-${Date.now()}`;
    
    const addNodeToTree = (node) => {
      if (node.id === targetNode.id) {
        const currentMaxItem = node.children.reduce((max, child) => {
            const childItem = parseInt(child.details?.item || 0, 10);
            return childItem > max ? childItem : max;
        }, 0);
        const nextItem = String(currentMaxItem + 1).padStart(3, '0');

        return {
          ...node,
          children: [...node.children, { 
            id: newNodeId, productCode: product.value, label: product.value, quantity: formData.quantity, uom: formData.uom,
            headerDetails: { base_unit: formData.base_unit, plant_location: formData.plant_location, alternative_bom: formData.alternative_bom, unit_of_measurement: product.uom }, 
            details: { item: nextItem, item_category: product.item_category, component_description: product.description, valid_from: formData.valid_from, valid_to: formData.valid_to, change_number: formData.change_number, assembly: "No" },
            children: [] 
          }],
        };
      }
      if (node.children && node.children.length > 0) return { ...node, children: node.children.map(addNodeToTree) };
      return node;
    };

    setBomTree((prevTree) => addNodeToTree(prevTree));
    setExpanded((prev) => ({ ...prev, [targetNode.id]: true }));
    setDetailsModal({ show: false, targetNode: null, product: null, formData: {} });
  };

  const handleRemoveNode = (e, nodeIdToRemove) => {
    e.stopPropagation();
    const removeNodeFromTree = (node) => {
      if (!node.children) return node;
      const filteredChildren = node.children.filter(child => child.id !== nodeIdToRemove);
      return { ...node, children: filteredChildren.map(removeNodeFromTree) };
    };
    setBomTree(prevTree => removeNodeFromTree(prevTree));
  };

  const handleSaveBOM = async () => {
    if (!bomTree) return;

    const bomDataArr = [];

    const extractBomLevels = (node) => {
        if (node.children && node.children.length > 0) {
            
            const components = node.children.map((child, idx) => {
                const fallbackItem = String(idx + 1).padStart(3, '0'); 
                const hasChildren = child.children && child.children.length > 0;
                
                return {
                    item: child.details?.item || fallbackItem, 
                    item_category: child.details?.item_category || "ROH",
                    component_product: child.productCode,
                    component_description: child.details?.component_description || "",
                    quantity: child.details?.quantity || child.quantity || 1,
                    uom: child.details?.uom || child.uom || "NOS",
                    assembly: hasChildren ? "Yes" : "No", 
                    valid_from: child.details?.valid_from || null,
                    valid_to: child.details?.valid_to || null,
                    change_number: child.details?.change_number || ""
                };
            });

            // FIXED: unit_of_measurement correctly extracted from headerDetails
            bomDataArr.push({
                product_code: node.productCode,
                base_unit: node.headerDetails?.base_unit || 1000,
                unit_of_measurement: node.headerDetails?.unit_of_measurement || "NOS",
                plant_location: node.headerDetails?.plant_location || "Penya",
                bom_usage: "Production",
                alternative_bom: node.headerDetails?.alternative_bom || "10",
                components: components
            });

            node.children.forEach(child => {
                extractBomLevels(child);
            });
        }
    };

    extractBomLevels(bomTree);

    try {
        const payload = { rfq_id: rfqId, rfq_item_id: rfqItemId, bomData: bomDataArr };
        await axios.post(`https://master.erp.sbeecables.com/api/bom/save-rfq-bom`, payload);
        toast.success("Custom BOM Saved Successfully!");
        setIsEditMode(false);
    } catch (error) {
        console.error("Save BOM Error:", error);
        toast.error("Failed to update Custom BOM. Ensure backend routes exist!");
    }
  };

  const renderNode = (node) => {
    if (!node) return null;
    const isExpanded = expanded[node.id];
    const isRoot = node.id === bomTree.id;

    return (
      <li key={node.id}>
        <div className="bom-card" onDragOver={isEditMode ? handleDragOver : undefined} onDrop={isEditMode ? (e) => handleDrop(e, node) : undefined} style={isEditMode ? { border: '1px dashed #3b82f6', cursor: 'pointer' } : { cursor: 'default' }}>
          {node.children && node.children.length > 0 && (
            <span className="toggle" onClick={() => toggleNode(node.id)} style={{ cursor: 'pointer' }}>
              {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
            </span>
          )}
          <FaCubes className="icon" />
          <span className="label" style={{ fontWeight: 600, marginRight: '10px' }}>{node.label}</span>
          <div style={{ flex: 1 }}></div>
          {isEditMode && !isRoot && (
              <FaTrash style={{ color: '#ef4444', marginLeft: '15px', cursor: 'pointer' }} onClick={(e) => handleRemoveNode(e, node.id)} />
          )}
        </div>
        {isExpanded && node.children && node.children.length > 0 && (
          <ul>{node.children.map((child) => renderNode(child))}</ul>
        )}
      </li>
    );
  };

  const content = (
    <div className="bom-container" style={isModal ? { padding: '0', display: 'flex', minHeight: '500px', background: '#fff', flexDirection: 'column' } : {}}>
      <div style={{ padding: '15px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc' }}>
          <h6 style={{ margin: 0, fontWeight: 600 }}>Master BOM: {preSelectedCode}</h6>
          {!isReadOnly && (
            <button className={`btn btn-sm ${isEditMode ? 'btn-danger' : 'btn-primary'}`} onClick={() => setIsEditMode(!isEditMode)}>
                <FaEdit style={{ marginRight: '5px' }} /> {isEditMode ? 'Cancel Edit' : 'Edit BOM'}
            </button>
          )}
      </div>

      <div style={{ display: 'flex', flex: 1 }}>
          {isEditMode && (
            <div style={{ width: '300px', borderRight: '1px solid #e2e8f0', background: '#f8fafc', padding: '20px', overflowY: 'auto' }}>
              <h6 style={{ fontWeight: 600, marginBottom: '20px' }}>Add Component</h6>
              <div style={{ marginBottom: '25px' }}>
                  <label style={{ fontSize: '13px', fontWeight: 500, marginBottom: '8px', display: 'block' }}>Search Product:</label>
                  <Select
                      options={productOptions}
                      placeholder="Select a product..."
                      onChange={(selected) => setSelectedDragProduct(selected)}
                      value={selectedDragProduct}
                      isClearable
                      menuPortalTarget={document.body} 
                      styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                  />
              </div>
              {selectedDragProduct && (
                  <div style={{ padding: '15px', background: '#eff6ff', borderRadius: '8px', border: '1px dashed #3b82f6' }}>
                      <label style={{ fontSize: '12px', color: '#1d4ed8', marginBottom: '10px', display: 'block', fontWeight: 600 }}>Drag to add →</label>
                      <div draggable onDragStart={(e) => handleDragStart(e, selectedDragProduct)} style={styles.dragBox}>
                          <FaGripLines style={{ color: '#94a3b8' }} /> {selectedDragProduct.label}
                      </div>
                  </div>
              )}
            </div>
          )}

          <div style={{ flex: 1, padding: '20px', overflowY: 'auto' }}>
            <div className="bom-tree" style={{ minHeight: '350px', display: 'flex', flexDirection: 'column' }}>
              {loading ? <p style={{ marginTop: '10px', fontWeight: 500, color: '#555' }}>Loading BOM...</p> : bomTree ? <ul>{renderNode(bomTree)}</ul> : null}
            </div>
          </div>
      </div>
    </div>
  );

  return (
    <>
      {isModal ? content : <Layout>{content}</Layout>}

      {altModal.show && (
         <div style={styles.modalOverlay}>
            <div style={styles.modalContent}>
                <h5 style={{ fontWeight: 600, marginBottom: '15px' }}>Alternate Products Available</h5>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '25px', maxHeight: '200px', overflowY: 'auto' }}>
                    <label style={styles.radioLabel}>
                        <input type="radio" name="alternateProduct" value={altModal.originalProduct.value} checked={altModal.selectedValue === altModal.originalProduct.value} onChange={() => setAltModal({ ...altModal, selectedValue: altModal.originalProduct.value, selectedLabel: altModal.originalProduct.label })} style={{ marginRight: '10px' }} />
                        <span style={{ fontWeight: 600, color: '#3b82f6' }}>Original:</span> {altModal.originalProduct.label}
                    </label>
                    {altModal.alternates.map((alt, idx) => (
                        <label key={idx} style={styles.radioLabel}>
                            <input type="radio" name="alternateProduct" value={alt.alternate_product_code} checked={altModal.selectedValue === alt.alternate_product_code} onChange={() => setAltModal({ ...altModal, selectedValue: alt.alternate_product_code, selectedLabel: alt.alternate_product_code })} style={{ marginRight: '10px' }}/>
                            <span style={{ fontWeight: 600 }}>Alternate:</span> {alt.alternate_product_code}
                        </label>
                    ))}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                    <button className="btn btn-outline-secondary btn-sm" onClick={() => setAltModal({ show: false, alternates: [], targetNode: null, originalProduct: null, selectedValue: "", selectedLabel: "" })}>Cancel</button>
                    <button className="btn btn-dark btn-sm" onClick={handleConfirmAlternate}>Confirm Selection</button>
                </div>
            </div>
        </div>
      )}
      
      {detailsModal.show && (
         <div style={styles.modalOverlay}>
            <div style={styles.modalContent}>
                <h5 style={{ fontWeight: 600, marginBottom: '15px', fontSize: '16px' }}>Component Details for {detailsModal.product?.label}</h5>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                    <div><label style={styles.smallLabel}>Quantity</label><input type="number" className="form-control form-control-sm" value={detailsModal.formData.quantity} onChange={(e) => setDetailsModal({...detailsModal, formData: {...detailsModal.formData, quantity: e.target.value}})} /></div>
                    <div><label style={styles.smallLabel}>UOM</label><input type="text" className="form-control form-control-sm" readOnly value={detailsModal.formData.uom} /></div>
                    <div><label style={styles.smallLabel}>Valid From</label><input type="date" className="form-control form-control-sm" value={detailsModal.formData.valid_from} onChange={(e) => setDetailsModal({...detailsModal, formData: {...detailsModal.formData, valid_from: e.target.value}})} /></div>
                    <div><label style={styles.smallLabel}>Valid To</label><input type="date" className="form-control form-control-sm" value={detailsModal.formData.valid_to} onChange={(e) => setDetailsModal({...detailsModal, formData: {...detailsModal.formData, valid_to: e.target.value}})} /></div>
                    <div style={{ gridColumn: '1 / -1' }}><label style={styles.smallLabel}>Change Number</label><input type="text" className="form-control form-control-sm" value={detailsModal.formData.change_number} onChange={(e) => setDetailsModal({...detailsModal, formData: {...detailsModal.formData, change_number: e.target.value}})} /></div>
                </div>

                <div style={{ gridColumn: '1 / -1', borderTop: '1px solid #ccc', margin: '15px 0 10px 0', paddingTop: '10px', fontSize: '14px', fontWeight: 600, color: '#334155' }}>
                    Sub-Assembly Info (If Applicable)
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '20px' }}>
                    <div><label style={styles.smallLabel}>Base Unit</label><input type="number" className="form-control form-control-sm" value={detailsModal.formData.base_unit} onChange={(e) => setDetailsModal({...detailsModal, formData: {...detailsModal.formData, base_unit: e.target.value}})} /></div>
                    <div><label style={styles.smallLabel}>Location</label><input type="text" className="form-control form-control-sm" value={detailsModal.formData.plant_location} onChange={(e) => setDetailsModal({...detailsModal, formData: {...detailsModal.formData, plant_location: e.target.value}})} /></div>
                    <div><label style={styles.smallLabel}>Alt BOM</label><input type="text" className="form-control form-control-sm" value={detailsModal.formData.alternative_bom} onChange={(e) => setDetailsModal({...detailsModal, formData: {...detailsModal.formData, alternative_bom: e.target.value}})} /></div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                    <button className="btn btn-outline-secondary btn-sm" onClick={() => setDetailsModal({ show: false, targetNode: null, product: null, formData: {} })}>Cancel</button>
                    <button className="btn btn-primary btn-sm" onClick={handleConfirmDropDetails}>Add to BOM</button>
                </div>
            </div>
        </div>
      )}

      <style>{`
        .bom-container{ background:#f5f7fb; min-height:100vh; }
        .bom-tree ul{ padding-left:30px; position:relative; margin-top:10px; }
        .bom-tree li{ list-style:none; margin:10px 0; position:relative; }
        .bom-tree li::before{ content:''; position:absolute; top:-10px; left:-15px; border-left:2px solid #e0e6ed; height:100%; }
        .bom-tree li::after{ content:''; position:absolute; top:20px; left:-15px; border-top:2px solid #e0e6ed; width:15px; }
        .bom-card{ display:inline-flex; align-items:center; background:#fff; padding:8px 14px; border-radius:8px; border:1px solid #e0e6ed; box-shadow:0 2px 6px rgba(0,0,0,0.08); transition:all .2s ease; min-width: 250px;}
        .bom-card:hover{ transform:translateY(-2px); box-shadow:0 6px 12px rgba(0,0,0,0.12); }
        .toggle{ margin-right:8px; color:#555; }
        .icon{ margin-right:8px; color:#3b82f6; }
      `}</style>
    </>
  );
});

const styles = {
    dragBox: { padding: '12px 15px', background: '#fff', border: '2px dashed #3b82f6', borderRadius: '6px', fontSize: '13px', cursor: 'grab', boxShadow: '0 4px 6px -1px rgba(59,130,246,0.1)', fontWeight: 500, color: '#1e293b', display: 'flex', alignItems: 'center', gap: '10px' },
    modalOverlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99999 },
    modalContent: { background: '#fff', padding: '25px', borderRadius: '10px', width: '550px', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' },
    smallLabel: { fontSize: '13px', fontWeight: 600, marginBottom: '5px', color: '#334155' },
    radioLabel: { display: 'flex', alignItems: 'center', padding: '10px 15px', border: '1px solid #cbd5e1', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', background: '#f8fafc', transition: 'background 0.2s' }
};

export default BomRFQ;